import { AxiosResponse } from "axios";

import { IDeviceTypeOption } from "../../@types/admin/AdminDevice";
import { ICodeOption } from "../../@types/admin/AdminMedication";
import { IRoleOption, ISelectOption } from "../../@types/admin/AdminStaff";
import { api } from "../api";

/** Raw shape of `SelectItemDto` — the API sends `id` as a string. */
interface IRawSelectItem {
  id: string;
  name: string;
}

function withParsedIds(
  response: AxiosResponse<IRawSelectItem[]>
): AxiosResponse<ISelectOption[]> {
  const items = Array.isArray(response.data) ? response.data : [];
  return {
    ...response,
    data: items.map((item) => ({
      id: Number.parseInt(String(item.id), 10),
      name: item.name,
    })),
  };
}

/**
 * Dropdown catalogues shared by the migrated /admin pages (EN-5150): hospital
 * groups and their wards, plus the other `/Retool/SelectValues` lists as later
 * pages need them.
 *
 * `SelectItemDto.Id` is a string so the endpoint can serve both int and Guid
 * keys; these two are int-keyed, so the ids are parsed here — the same fix
 * Retool applied in each query's transformer.
 */
export const selectValuesService = {
  /**
   * Pharmaceutical forms a custom medication can declare (EN-5240). String
   * codes, so not parsed; `name` is the English label.
   */
  async getMedicationFormOptions(): Promise<AxiosResponse<ICodeOption[]>> {
    return api.get<ICodeOption[]>("/Retool/SelectValues/MedicationForms");
  },

  /** Languages translations can be written in, e.g. `nl-NL` (EN-5240). */
  async getLanguageOptions(): Promise<AxiosResponse<ICodeOption[]>> {
    return api.get<ICodeOption[]>("/Retool/SelectValues/Languages");
  },

  async getHospitalOptions(): Promise<AxiosResponse<ISelectOption[]>> {
    const response = await api.get<IRawSelectItem[]>(
      "/Retool/SelectValues/Hospitals"
    );
    return withParsedIds(response);
  },

  /**
   * Roles assignable to a staff member, from the Global role catalogue.
   * `SelectItemDto.Id` carries the role CODE here (not a numeric key), so
   * these are mapped to `{ code, name }` rather than parsed as ids.
   */
  async getRoleOptions(): Promise<AxiosResponse<IRoleOption[]>> {
    const response = await api.get<IRawSelectItem[]>(
      "/Retool/SelectValues/Roles"
    );
    const items = Array.isArray(response.data) ? response.data : [];
    return {
      ...response,
      data: items.map((item) => ({ code: String(item.id), name: item.name })),
    };
  },

  /** Country catalogue for the hospital forms. */
  async getCountryOptions(): Promise<AxiosResponse<ISelectOption[]>> {
    const response = await api.get<IRawSelectItem[]>(
      "/Retool/SelectValues/Countries"
    );
    return withParsedIds(response);
  },

  /** Wards of one hospital group (`cms_hospital` rows under it). */
  async getWardOptions(
    hospitalGroupId: number
  ): Promise<AxiosResponse<ISelectOption[]>> {
    const response = await api.get<IRawSelectItem[]>(
      "/Retool/SelectValues/Wards",
      { params: { HospitalGroupId: hospitalGroupId } }
    );
    return withParsedIds(response);
  },

  /**
   * Every ward, across all hospital groups. `HospitalGroupId` is optional on
   * the endpoint (`GetHospitalsByGroupRequestDto`), and the Retool Studies
   * and Ward Measurement Types pages both call it unfiltered — a study or a
   * measurement-type config is attached to a ward without picking its
   * hospital first.
   */
  async getAllWardOptions(): Promise<AxiosResponse<ISelectOption[]>> {
    const response = await api.get<IRawSelectItem[]>(
      "/Retool/SelectValues/Wards"
    );
    return withParsedIds(response);
  },

  /**
   * Device types for the add-device form (EN-5166). Retool read
   * `PatientDeviceTypes` directly and got archived types too; the endpoint
   * filters them out, and an archived type should not be assignable.
   *
   * These ids are Guids, so they are NOT parsed as ints like the other
   * catalogues.
   */
  async getPatientDeviceTypeOptions(): Promise<
    AxiosResponse<IDeviceTypeOption[]>
  > {
    const response = await api.get<IRawSelectItem[]>(
      "/Retool/SelectValues/PatientDeviceTypes"
    );
    const items = Array.isArray(response.data) ? response.data : [];
    return {
      ...response,
      data: items.map((item) => ({ id: String(item.id), name: item.name })),
    };
  },

  /** Condition-type catalogue for the Studies form. */
  async getConditionTypeOptions(): Promise<AxiosResponse<ISelectOption[]>> {
    const response = await api.get<IRawSelectItem[]>(
      "/Retool/SelectValues/ConditionTypes"
    );
    return withParsedIds(response);
  },
};
